import { NgIf } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IDropdownOption, IFormAutofillMapping } from 'scw-digital-boards-widgets';
import { TrimmedRequiredValidator } from '../../../../../../core/validators/trimmed-required.validator';
import { CustomMatErrorComponent } from '../../../../../../shared/components/custom-mat-error/custom-mat-error.component';
import { MatDropdown } from '../../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { IFormField } from '../../../../../dashboard-content/widget-add-edit-dialog/input-components/form-field-dropdown/form-field-dropdown.store';
import { IWidgetRequestDropdownOption } from '../form-autofill-add-edit-dialog.interface';
import { FilterAvailableFieldsPipe } from './filter-available-fields.pipe';

@Component({
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    MatMiniFabButton,
    MatTooltip,
    MatDropdown,
    MatFormField,
    MatInput,
    MatIcon,
    MatButton,
    MatLabel,
    FilterAvailableFieldsPipe,
    CustomMatErrorComponent,
    NgIf,
  ],
  selector: 'app-form-autofill-mapping',
  standalone: true,
  styleUrls: ['./form-autofill-mapping.component.scss'],
  templateUrl: './form-autofill-mapping.component.html',
})
export class FormAutofillMappingComponent implements OnInit {
  public formControl = input<FormControl<IFormAutofillMapping[] | null>>(
    new FormControl<IFormAutofillMapping[] | null>([]),
  );
  public selectedFormId = input<number | null | undefined>(null);
  public formFields = input<IFormField[]>([]);
  public widgetRequests = input<IWidgetRequestDropdownOption[]>([]);
  public isFormSubmitted = input<boolean>(false);
  public allFormFieldOptions = computed<IDropdownOption<string>[]>(() =>
    this.formFields().map((field) => ({
      id: field.key,
      name: field.label,
    })),
  );

  public translate: TranslateService = inject(TranslateService);
  public formFieldsDropdownConfiguration: IDropdownSettings = this.getFormFieldsDropdownSettings();
  public widgetRequestsDropdownConfiguration: IDropdownSettings = this.getWidgetRequestsDropdownSettings();
  public internalForm: FormGroup;

  private destroyRef: DestroyRef = inject(DestroyRef);
  private previousFormId: number | null | undefined = null;

  constructor() {
    this.internalForm = new FormGroup({
      configurations: new FormArray<FormGroup>([]),
    });

    effect(() => this.handleInitialDataLoad());
    effect(() => this.handleFormIdChange());
    effect(() => {
      if (this.isFormSubmitted()) {
        this.internalForm.markAllAsTouched();
      }
    });
  }

  public get configurations(): FormArray {
    return this.internalForm.get('configurations') as FormArray;
  }

  public ngOnInit(): void {
    this.internalForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateExternalValue());
  }

  public addConfiguration(initialValue?: IFormAutofillMapping): void {
    const selectedField: IDropdownOption<string> | undefined = this.allFormFieldOptions().find(
      (field) => field.id.toString().toLowerCase() === initialValue?.formFieldKey?.toString().toLowerCase(),
    );

    const selectedWidget: IWidgetRequestDropdownOption | undefined = this.widgetRequests().find(
      (widgetRequest) =>
        widgetRequest.id.toString() === (initialValue?.widgetRequest?.id ?? initialValue?.widgetRequestId)?.toString(),
    );

    const group = new FormGroup({
      content: new FormControl(initialValue?.content ?? '', [
        Validators.required,
        TrimmedRequiredValidator(),
        Validators.maxLength(255),
      ]),
      formField: new FormControl(selectedField ? [selectedField] : null, [Validators.required]),
      widgetRequest: new FormControl(selectedWidget ? [selectedWidget] : null, [Validators.required]),
    });

    this.setDefaultContentOnWidgetChange(group);
    this.configurations.push(group);
  }

  private setDefaultContentOnWidgetChange(group: FormGroup): void {
    group
      .get('widgetRequest')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((widget) => {
        if (widget && widget[0]?.content) {
          group.get('content')?.setValue(widget[0].content, { emitEvent: false });
          this.updateExternalValue();
        }
      });
  }

  public removeConfiguration(index: number): void {
    if (index >= 0 && index < this.configurations.length) {
      this.configurations.removeAt(index);
      this.updateExternalValue();
    }
  }

  private updateExternalValue(): void {
    if (!this.formControl()) {
      return;
    }

    const value: IFormAutofillMapping[] = this.configurations.controls.map((group: AbstractControl) => {
      const formGroup = group as FormGroup;
      const formFieldVal = formGroup.get('formField')?.value as IDropdownOption<string>[] | null;
      const widgetRequestVal = formGroup.get('widgetRequest')?.value as IWidgetRequestDropdownOption[] | null;

      return {
        content: formGroup.get('content')?.value,
        formFieldKey: formFieldVal?.[0]?.id ?? null,
        widgetRequestId: widgetRequestVal?.[0]?.id ?? null,
      };
    });

    this.formControl()?.setValue(value.length ? value : []);
    this.formControl()?.setErrors(this.internalForm.invalid ? { incomplete: true } : null);
    this.formControl()?.markAsDirty();
  }

  private getFormFieldsDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: true,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: true,
      text: this.translate.instant('page.widgetContent.addEditDialog.formFieldDropdown.label'),
    };
  }

  private getWidgetRequestsDropdownSettings(): IDropdownSettings {
    return {
      enableSearchFilter: true,
      isClientSide: true,
      repositionDropdownList: true,
      singleSelection: true,
      text: this.translate.instant('field.endpoint'),
    };
  }

  private handleInitialDataLoad(): void {
    const fields: IDropdownOption<string>[] = this.allFormFieldOptions();
    const requests: IWidgetRequestDropdownOption[] = this.widgetRequests();
    const initialValues: IFormAutofillMapping[] | null | undefined = this.formControl()?.value;
    const isEditMode: boolean = Array.isArray(initialValues) && initialValues.length > 0;

    if (this.configurations.length > 0) {
      return;
    }

    if (isEditMode) {
      if (fields.length > 0 && requests.length > 0) {
        initialValues?.forEach((val) => this.addConfiguration(val));
      }
    } else {
      this.addConfiguration();
    }
  }

  private handleFormIdChange(): void {
    const currentId: number | null | undefined = this.selectedFormId();

    if (this.configurations.length > 0 && currentId !== this.previousFormId) {
      this.configurations.clear();
      this.addConfiguration();
    }

    this.previousFormId = currentId;
  }
}
